import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { IconOrbit } from '../components/icons';
import { Field, LanguageToggle, TextInput } from '../components/ui';
import { t } from '../i18n';
import { messageOf } from '../lib/format';

function AuthShell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="auth-card__head">
          <Link className="brand" to="/">
            <span className="brand__mark">
              <IconOrbit size={24} />
            </span>
            my&#8209;lib&nbsp;space
          </Link>
          <h1>{title}</h1>
        </div>
        {children}
      </div>
    </div>
  );
}

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(identifier.trim(), password);
      navigate('/app', { replace: true });
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell title={t('auth.login')}>
      <form className="stack" onSubmit={onSubmit}>
        <Field label={t('auth.identifier')} htmlFor="identifier">
          <TextInput
            id="identifier"
            autoComplete="username"
            autoCapitalize="none"
            required
            value={identifier}
            invalid={Boolean(error)}
            onChange={(event) => setIdentifier(event.target.value)}
          />
        </Field>
        <Field label={t('auth.password')} htmlFor="password" error={error ?? undefined}>
          <TextInput
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            invalid={Boolean(error)}
            onChange={(event) => setPassword(event.target.value)}
          />
        </Field>
        <button type="submit" className="btn btn--primary btn--block btn--lg" disabled={busy}>
          {busy ? t('auth.checking') : t('nav.enter')}
        </button>
        <p className="small muted center">
          {t('auth.noAccount')}{' '}
          <Link to="/register">{t('auth.create')}</Link>
        </p>
      </form>
      <div className="row" style={{ justifyContent: 'center' }}>
        <LanguageToggle />
      </div>
    </AuthShell>
  );
}

export function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ displayName: '', username: '', email: '', password: '' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const set = (key: keyof typeof form) => (event: { target: { value: string } }) =>
    setForm((current) => ({ ...current, [key]: event.target.value }));

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await register({
        email: form.email.trim(),
        username: form.username.trim().toLowerCase(),
        password: form.password,
        displayName: form.displayName.trim() || undefined,
      });
      navigate('/app', { replace: true });
    } catch (caught) {
      setError(messageOf(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell title={t('auth.register')}>
      <form className="stack" onSubmit={onSubmit}>
        <Field label={t('auth.displayName')} htmlFor="displayName" hint={t('auth.displayNameHint')}>
          <TextInput id="displayName" autoComplete="name" value={form.displayName} onChange={set('displayName')} />
        </Field>
        <Field
          label={t('auth.username')}
          htmlFor="username"
          hint={t('auth.usernameHint')}
          error={error ?? undefined}
        >
          <TextInput
            id="username"
            autoComplete="username"
            autoCapitalize="none"
            pattern="[a-z0-9_\-]{3,30}"
            required
            invalid={Boolean(error)}
            value={form.username}
            onChange={set('username')}
          />
        </Field>
        <Field label={t('auth.email')} htmlFor="email">
          <TextInput id="email" type="email" autoComplete="email" required value={form.email} onChange={set('email')} />
        </Field>
        <Field label={t('auth.password')} htmlFor="password" hint={t('auth.passwordHint')}>
          <TextInput
            id="password"
            type="password"
            autoComplete="new-password"
            minLength={8}
            required
            value={form.password}
            onChange={set('password')}
          />
        </Field>
        <button type="submit" className="btn btn--primary btn--block btn--lg" disabled={busy}>
          {busy ? t('auth.creating') : t('nav.start')}
        </button>
        <p className="small muted center">
          {t('auth.haveAccount')}{' '}
          <Link to="/login">{t('nav.enter')}</Link>
        </p>
      </form>
      <div className="row" style={{ justifyContent: 'center' }}>
        <LanguageToggle />
      </div>
    </AuthShell>
  );
}
